// src/components/TemplateCardForYou.jsx
import { useEffect, useMemo, useState } from "react";
import API from "../config/api.js";

export default function TemplateCardForYou({
  template,
  day,
  month,
  onUse,
  onToggleQueue,
  scheduledInfo,
}) {
  const idTemplate = template?.idtemplate || "";

  const [iddraft, setIddraft] = useState(
    scheduledInfo?.iddraft || template?.iddraft || ""
  );
  const [isQueued, setIsQueued] = useState(
    Boolean(scheduledInfo?.iddraft || template?.iddraft)
  );
  const [scheduledText, setScheduledText] = useState(
    scheduledInfo?.scheduledText || ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIddraft(scheduledInfo?.iddraft || template?.iddraft || "");
    setIsQueued(Boolean(scheduledInfo?.iddraft || template?.iddraft));
    setScheduledText(scheduledInfo?.scheduledText || "");
  }, [scheduledInfo, template]);

  const getAbsoluteUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API.baseUrl}/${path}`;
  };

  const getTemplateImageUrl = (path, useOriginal = false) => {
    if (!path) return "/assets/img/260x325.png";

    const finalPath = useOriginal
      ? path
      : path.replace("original.jpg", "260x325.jpg");

    return getAbsoluteUrl(finalPath);
  };

  const images = useMemo(() => {
    if (!template) return ["/assets/img/260x325.png"];

    const list = [];
    list.push(getTemplateImageUrl(template.image, true));

    if (template.type === "template" || template.type === "images") {
      if (template.image2) {
        list.push(getTemplateImageUrl(template.image2));
      }

      if (template.image3) {
        list.push(getTemplateImageUrl(template.image3));
      }
    }

    return list;
  }, [template]);

  const handleQueueToggle = async (e) => {
    const checked = e.target.checked;

    if (!onToggleQueue || isSubmitting) return;

    setIsQueued(checked);
    setIsSubmitting(true);

    const result = await onToggleQueue({
      template,
      checked,
      iddraft,
    });

    if (result?.success) {
      setIddraft(result.iddraft || "");
      setScheduledText(result.scheduledText || "");
      setIsQueued(checked);
    } else {
      setIsQueued(!checked);
    }

    setIsSubmitting(false);
  };

  const handleUse = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onUse?.(template);
  };

  if (!template) return null;

  return (
    <div
      className={`card h-100 w-100 suggested template template-${idTemplate} rounded-4 animate__animated animate__fadeIn animate__slow`}
      data-idimages={
        Array.isArray(template.idimages)
          ? template.idimages.join(",")
          : typeof template.idimages === "string"
          ? template.idimages
          : ""
      }
      data-iddraft={iddraft}
    >
      <div
        id={`carousel-template-${idTemplate}`}
        className="carousel slide"
        data-bs-interval="500"
        onMouseEnter={(e) => {
          if (window.bootstrap?.Carousel) {
            const instance = window.bootstrap.Carousel.getOrCreateInstance(
              e.currentTarget
            );
            instance.cycle();
          }
        }}
        onMouseLeave={(e) => {
          if (window.bootstrap?.Carousel) {
            const instance = window.bootstrap.Carousel.getOrCreateInstance(
              e.currentTarget
            );
            instance.pause();
          }
        }}
      >
        <div className="carousel-inner">
          {images.map((image, index) => (
            <div
              key={`${idTemplate}-${index}`}
              className={`carousel-item ${index === 0 ? "active" : ""}`}
            >
              <img
                src={image}
                alt=""
                onError={(e) => {
                  e.currentTarget.src = "/assets/img/260x325.png";
                }}
                className="img-fluid w-100 bg-loading rounded-3"
                width="260"
                height="325"
                style={{ objectFit: "cover" }}
              />
            </div>
          ))}
        </div>

        {(template.type === "template" || template.type === "images") &&
          images.length > 1 && (
            <>
              <button
                className="carousel-control-prev"
                type="button"
                data-bs-target={`#carousel-template-${idTemplate}`}
                data-bs-slide="prev"
              >
                <span
                  className="carousel-control-prev-icon"
                  aria-hidden="true"
                ></span>
                <span className="visually-hidden">Previous</span>
              </button>

              <button
                className="carousel-control-next"
                type="button"
                data-bs-target={`#carousel-template-${idTemplate}`}
                data-bs-slide="next"
              >
                <span
                  className="carousel-control-next-icon"
                  aria-hidden="true"
                ></span>
                <span className="visually-hidden">Next</span>
              </button>
            </>
          )}

        <div
          style={{ zIndex: 4 }}
          className="position-absolute top-0 start-0 end-0 m-1 card-actions"
        >
          <div className="mt-2 px-2 py-1 action-buttons d-flex align-items-center">
            <div className="form-check d-inline-block ms-2">
              <input
                className="form-check-input mt-0 fs-4"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-title="Add to queue"
                type="checkbox"
                role="switch"
                name="posted"
                checked={isQueued}
                disabled={isSubmitting}
                onChange={handleQueueToggle}
              />
            </div>

            <button
              style={{ marginBottom: "1px" }}
              type="button"
              className="btn me-3 use-button"
              onClick={handleUse}
            >
              <img
                src="/assets/img/circle-plus.png"
                width="26"
                height="26"
                alt="add"
              />
            </button>
          </div>

          <div className="ms-1 px-2 text-white scheduled-date-time float-start">
            {scheduledText}
          </div>
        </div>

        <div
          style={{ zIndex: 3 }}
          className="position-absolute top-0 end-0 p-2 text-white scheduled-date-time"
        >
          {day && month ? (
            <div
              className="bg-white text-dark rounded text-center lh-1 p-2"
              style={{ width: "45px", height: "41px", fontSize: "12px" }}
            >
              <strong>{day}</strong>
              <br />
              {month}
            </div>
          ) : null}
        </div>

        <div
          style={{ zIndex: 4 }}
          className="position-absolute bottom-0 start-0 end-0 p-2 m-1"
        >
          <h6
            className="text-white"
            style={{ fontWeight: 700, fontSize: "12px" }}
          >
            Carousel • {template.category_caption || template.title || ""}
          </h6>

          <p className="card-text caption-content text-white">
            {template.caption || ""}
          </p>
        </div>
      </div>
    </div>
  );
}